import React, { useState, useEffect } from "react";
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

const PayPal = ({ price, sellerId, sellerName, orderNumber, shippingInfo, currencySymbol = "$" }) => {
  const [error, setError] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [adjustedPrice, setAdjustedPrice] = useState(price);
  const [originalPrice, setOriginalPrice] = useState(price);
  const [exchangeRateApplied, setExchangeRateApplied] = useState(false);
  const [paypalComponents, setPaypalComponents] = useState(null);
  const navigate = useNavigate();

  // Dynamically import PayPal components to prevent runtime errors
  useEffect(() => {
    const loadPayPalComponents = async () => {
      try {
        const { PayPalScriptProvider, PayPalButtons } = await import('@paypal/react-paypal-js');
        setPaypalComponents({ PayPalScriptProvider, PayPalButtons });
      } catch (err) {
        console.error('Failed to load PayPal components:', err);
        setError('PayPal components failed to load');
      }
    };

    loadPayPalComponents();

    // Add global error handler for PayPal popup errors
    const handleGlobalError = (event) => {
      if (event.error && typeof event.error === 'object') {
        const errorMessage = event.error.message || event.error.toString();
        if (errorMessage.includes('popup') || errorMessage.includes('PayPal')) {
          console.log('PayPal popup error caught:', errorMessage);
          event.preventDefault();
          return false;
        }
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalError);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalError);
    };
  }, []);

  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        // Use sellerId from props for API, but sellerName for URLs
        if (!sellerId) {
          setError('Seller ID not found');
          return;
        }
        // Fetch seller's enabled payment methods (includes PayPal config)
        const { data } = await api.get(`/seller/${sellerId}/payment-methods`);
        
        if (data.success && data.config.paypal && data.config.paypal.isEnabled) {
          setPaypalConfig(data.config.paypal);
          
          // Apply exchange rate to price
          let finalPrice = price;
          let rateApplied = false;
          
          if (data.config.paypal.exchangeRate && data.config.paypal.exchangeRate !== 1.0) {
            finalPrice = parseFloat((price * data.config.paypal.exchangeRate).toFixed(2));
            rateApplied = true;
            console.log(`PayPal Exchange Rate Applied: ${price} × ${data.config.paypal.exchangeRate} = ${finalPrice}`);
          }
          
          setAdjustedPrice(finalPrice);
          setOriginalPrice(price);
          setExchangeRateApplied(rateApplied);
          setIsScriptLoaded(true);
        } else {
          setError('PayPal is not enabled or configured for this seller');
        }
      } catch (error) {
        console.error('Error fetching PayPal config:', error);
        setError('Failed to load PayPal configuration');
      }
    };

    fetchPayPalConfig();
  }, [price, sellerId]);

  if (!paypalConfig) {
    return (
      <div className="mt-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p>Loading PayPal...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 text-red-500 text-center">
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Simple PayPal configuration
  const initialOptions = {
    "client-id": paypalConfig.clientId,
    currency: "USD",
    intent: "capture",
    components: "buttons"
  };

  const createOrder = async () => {
    try {
      const { data } = await api.post('/payment/create-paypal-order', {
        price: adjustedPrice,
        orderNumber,
        sellerId: sellerId, // API only
        shippingInfo
      });
      return data.orderId;
    } catch (error) {
      console.error("PayPal order creation error:", error);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      const { data: captureData } = await api.post('/payment/capture-paypal-payment', { 
        orderId: data.orderID,
        sellerId: sellerId // API only
      });

      if (captureData.message === "Payment captured successfully") {
        // Store order number in localStorage for confirmation page
        localStorage.setItem('orderNumber', orderNumber);
        
        try {
          // Prepare payment details from PayPal capture response
          const paymentDetails = {
            transactionId: data.orderID,
            paymentId: data.orderID,
            amount: adjustedPrice,
            originalAmount: originalPrice,
            exchangeRate: paypalConfig.exchangeRate,
            exchangeRateApplied: exchangeRateApplied,
            fee: captureData.capture?.seller_receivable_breakdown?.paypal_fee?.value ? parseFloat(captureData.capture.seller_receivable_breakdown.paypal_fee.value) : null,
            netAmount: captureData.capture?.seller_receivable_breakdown?.gross_amount?.value ? parseFloat(captureData.capture.seller_receivable_breakdown.gross_amount.value) : adjustedPrice,
            gateway: 'paypal',
            status: 'completed'
          };

          // Update order status with payment details
          await api.put(`/order/order-confirm/${orderNumber}`, { 
            paymentMethod: 'paypal',
            paymentDetails: paymentDetails
          });
          
          // Navigate to confirmation page with PayPal success parameter
          navigate(`/${sellerName}/order/confirm?payment_method=paypal&status=success`);
        } catch (orderError) {
          console.error("Error confirming order:", orderError);
          // Payment was successful but order confirmation failed
          navigate(`/${sellerName}/order/confirm?payment_method=paypal&status=success&order_confirmed=false`);
        }
      } else {
        throw new Error('Payment capture failed');
      }
    } catch (error) {
      console.error("Error capturing payment:", error);
      // Navigate to confirmation page with error
      navigate(`/${sellerName}/order/confirm?payment_method=paypal&status=error&error=${encodeURIComponent(error.message)}`);
    }
  };

  // Render PayPal button only if components are loaded
  const renderPayPalButton = () => {
    if (!paypalComponents || !isScriptLoaded || !paypalConfig.clientId) {
      return (
        <div className="mt-4 text-center text-gray-500">
          <p>Loading PayPal button...</p>
          <p className="text-xs">Components: {paypalComponents ? 'Loaded' : 'Loading'}</p>
          <p className="text-xs">Script: {isScriptLoaded ? 'Ready' : 'Loading'}</p>
          <p className="text-xs">Client ID: {paypalConfig?.clientId ? 'Set' : 'Not set'}</p>
        </div>
      );
    }

    try {
      const { PayPalScriptProvider, PayPalButtons } = paypalComponents;
      
      return (
        <div className="paypal-container">
          <PayPalScriptProvider options={initialOptions}>
            <PayPalButtons
              createOrder={createOrder}
              onApprove={onApprove}
              onError={(err) => {
                console.error("PayPal error:", err);
                // Only set error for actual payment failures, not window closing
                if (err && err.message && !err.message.includes('popup')) {
                  setError("PayPal payment failed. Please try again.");
                }
              }}
              onCancel={() => {
                console.log("PayPal payment cancelled");
                // Don't set error state for cancellation, just log it
                // This prevents the error from showing to the user
              }}
              style={{ layout: "horizontal" }}
            />
          </PayPalScriptProvider>
        </div>
      );
    } catch (err) {
      console.error('Error rendering PayPal button:', err);
      return (
        <div className="mt-4 text-red-500 text-center">
          <p>Failed to render PayPal button</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Try Again
          </button>
        </div>
      );
    }
  };

  return (
    <div className="mt-4">
      {/* Exchange Rate Information Display */}
      {exchangeRateApplied && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between text-sm">
            <span className="text-blue-800">
              <strong>Exchange Rate Applied:</strong> {currencySymbol}{originalPrice} × {paypalConfig.exchangeRate} = ${adjustedPrice}
            </span>
            <span className="text-blue-600 text-xs">
              USD
            </span>
          </div>
        </div>
      )}
      
      {/* PayPal Button */}
      {renderPayPalButton()}
      

    </div>
  );
};

export default PayPal;